import React, { useState, useEffect } from 'react';
import {
    Plus, Edit2, Trash2, UserPlus, X,
    Users, Shield, Activity, ChevronDown, AlertTriangle, MoreVertical
} from 'lucide-react';
import ReusableTable from '../components/ReusableTable';
import { mockUsers } from '../dummy/dummy';

// --- HELPER: GENERATE AVATAR & INITIALS ---
const getInitials = (name) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase() || 'U';
};

const getAvatarGradient = (name) => {
    const colors = [
        'from-indigo-500 to-blue-500',
        'from-emerald-400 to-teal-500',
        'from-rose-400 to-red-500',
        'from-amber-400 to-orange-500',
        'from-purple-500 to-pink-500'
    ];
    const charCode = name.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
};


// --- MAIN COMPONENT ---
const TeamManagementView = () => {
    const users = mockUsers;
    const [pageIsLoading, setPageIsLoading] = useState(false);

    // Fallback mock data if users array is empty
    const defaultUsers = [
        { id: '1', name: 'Alex Klein', email: 'alex@example.com', role: 'Admin', status: 'Active' },
        { id: '2', name: 'Sara Patel', email: 'sara@example.com', role: 'Reviewer', status: 'Active' },
        { id: '3', name: 'Michael Johnson', email: 'michael@example.com', role: 'User', status: 'Inactive' },
    ];

    const [team, setTeam] = useState(users.length > 0 ? users : defaultUsers);

    // Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const initialFormState = { id: '', name: '', email: '', role: 'User', status: 'Active' };
    const [formData, setFormData] = useState(initialFormState);

    const openAddModal = () => {
        setFormData(initialFormState);
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const openEditModal = (user) => {
        setFormData({ ...user, status: user.status || 'Active' });
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        setPageIsLoading(true);

        setTimeout(() => {
            if (isEditing) {
                setTeam(team.map(u => u.id === formData.id ? { ...formData } : u));
            } else {
                const newUser = {
                    ...formData,
                    id: `user_${Date.now()}`,
                };
                setTeam([newUser, ...team]);
            }
            setIsModalOpen(false);
            setPageIsLoading(false);
        }, 600);
    };

    const executeDelete = () => {
        if (!userToDelete) return;
        setPageIsLoading(true);

        setTimeout(() => {
            setTeam(team.filter(u => u.id !== userToDelete.id));
            setUserToDelete(null);
            setPageIsLoading(false);
        }, 500);
    };

    // --- Pagination States & Calculations ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const totalPages = Math.ceil(team.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentTeam = team.slice(indexOfFirstItem, indexOfLastItem);

    const [openDropdownId, setOpenDropdownId] = useState(null);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const columns = [
        {
            key: 'userDetails',
            title: 'User Details',
            headerClassName: 'pl-6 sm:pl-8',
            cellClassName: 'pl-6 sm:pl-8',
            render: (user) => (
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-700 flex items-center justify-center font-bold text-sm ring-2 ring-white shadow-sm shrink-0">
                        {getInitials(user.name)}
                    </div>
                    <div>
                        <div className="text-sm font-extrabold text-slate-900 mb-0.5">{user.name}</div>
                        <div className="text-xs font-semibold text-slate-500">{user.email}</div>
                    </div>
                </div>
            ),
        },
        {
            key: 'role',
            title: 'System Role',
            render: (user) => (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ring-1 shadow-sm ${user.role === 'Admin' ? 'bg-indigo-50 text-indigo-700 ring-indigo-200/50' :
                    user.role === 'Reviewer' ? 'bg-blue-50 text-blue-700 ring-blue-200/50' :
                        'bg-slate-50 text-slate-700 ring-slate-200/60'
                    }`}>
                    {user.role === 'Admin' ? <Shield size={12} /> : user.role === 'Reviewer' ? <Activity size={12} /> : <Users size={12} />}
                    {user.role}
                </span>
            ),
        },
        {
            key: 'status',
            title: 'Account Status',
            render: (user) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-black uppercase tracking-wider ring-1 shadow-sm ${(user.status === 'Active' || user.status === undefined) ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/50' : 'bg-rose-50 text-rose-700 ring-rose-200/50'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${(user.status === 'Active' || user.status === undefined) ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'
                        }`}></span>
                    {user.status || 'Active'}
                </span>
            ),
        },
        {
            key: 'actions',
            title: 'Actions',
            headerClassName: 'pr-6 sm:pr-8 text-right',
            cellClassName: 'pr-6 sm:pr-8 text-right',
            render: (user) => (
                <div className="relative inline-flex justify-end">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            // Toggle the dropdown for this specific user
                            setOpenDropdownId(openDropdownId === user.id ? null : user.id);
                        }}
                        className="p-2 bg-white ring-1 ring-transparent hover:ring-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all"
                    >
                        <MoreVertical className="w-5 h-5" />
                    </button>

                    {openDropdownId === user.id && (
                        <div
                            // 2. CRITICAL FIX: Changed 'right-6' to 'right-0' and ensured high z-index
                            className="absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-xl ring-1 ring-slate-200/60 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-[9999] py-2 w-48 text-left animate-in fade-in slide-in-from-top-2"
                            style={{ height: "max-content" }}
                        >
                            <button
                                onClick={() => {
                                    setOpenDropdownId(null); // Close dropdown
                                    openEditModal(user);
                                }}
                                className="w-full px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 flex items-center gap-3 transition-colors"
                            >
                                <Edit2 size={16} /> Edit User
                            </button>

                            <div className="h-px bg-slate-100 my-1 mx-2" />
                            <button
                                onClick={() => {
                                    setOpenDropdownId(null); // Close dropdown
                                    setUserToDelete(user);
                                }}
                                className="w-full px-4 py-2.5 text-sm font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-3 transition-colors"
                            >
                                <Trash2 size={16} /> Delete User
                            </button>
                        </div>
                    )}
                </div>
            ),
        }
    ];

    return (
        <div className="bg-slate-50  relative">

            {/* --- HERO HEADER --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white hidden sm:block">
                        <Users size={28} strokeWidth={2} />
                    </div>
                    <div>
                        <h3 className="text-2xl font-black text-slate-900 tracking-tight">Team Management</h3>
                        <p className="text-sm font-semibold text-slate-500 mt-0.5">Manage users, roles, and access across your organization.</p>
                    </div>
                </div>
                <button
                    onClick={openAddModal}
                    className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                >
                    <Plus size={18} />
                    Add New User
                </button>
            </div>

            {/* --- MAIN TABLE CARD --- */}
            <ReusableTable
                columns={columns}
                data={currentTeam}
                pagination={{
                    currentPage,
                    totalPages,
                    onPageChange: paginate,
                    indexOfFirstItem,
                    indexOfLastItem,
                    totalItems: team.length
                }}
            />
            {/* <div className="bg-white rounded-2xl  shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col min-h-[500px]">
                <div className="overflow-x-auto custom-scrollbar flex-1">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                        <thead>
                            <tr className="bg-slate-50/80 border-b border-slate-200">
                                <th className="p-4 pl-6 sm:pl-8 text-xs font-black text-slate-400 uppercase tracking-wider">User Details</th>
                                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">System Role</th>
                                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Account Status</th>
                                <th className="p-4 pr-6 sm:pr-8 text-xs font-black text-slate-400 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {team.map((user, idx) => (
                                <tr key={user.id} className="hover:bg-slate-50/50 transition-colors group cursor-default animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${idx * 50}ms` }}>

                             
                                    <td className="p-4 pl-6 sm:pl-8">
                                        <div className="flex items-center gap-4">
                                           
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-700 flex items-center justify-center font-bold text-sm ring-2 ring-white shadow-sm">
                                                {getInitials(user.name)}
                                            </div>
                                            <div>
                                                <div className="text-sm font-extrabold text-slate-900 mb-0.5">{user.name}</div>
                                                <div className="text-xs font-semibold text-slate-500">{user.email}</div>
                                            </div>
                                        </div>
                                    </td>

                                    <td className="p-4">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ring-1 shadow-sm ${user.role === 'Admin' ? 'bg-indigo-50 text-indigo-700 ring-indigo-200/50' :
                                            user.role === 'Reviewer' ? 'bg-blue-50 text-blue-700 ring-blue-200/50' :
                                                'bg-slate-50 text-slate-700 ring-slate-200/60'
                                            }`}>
                                            {user.role === 'Admin' ? <Shield size={12} /> : user.role === 'Reviewer' ? <Activity size={12} /> : <Users size={12} />}
                                            {user.role}
                                        </span>
                                    </td>

                                    <td className="p-4">
                                        
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-black uppercase tracking-wider ring-1 shadow-sm ${(user.status === 'Active'|| user.status === undefined) ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/50' :
                                                'bg-rose-50 text-rose-700 ring-rose-200/50'
                                            }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${(user.status === 'Active' ||  user.status === undefined) ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'}`}></span>
                                            {user.status || 'Active'}
                                        </span>
                                    </td>

                                    <td className="p-4 pr-6 sm:pr-8 text-right">
                                        <div className="flex justify-end items-center gap-2">
                                            <button
                                                onClick={() => openEditModal(user)}
                                                className="p-2.5 text-slate-400 bg-white ring-1 ring-transparent hover:text-indigo-600 hover:bg-indigo-50 hover:ring-indigo-200 rounded-xl transition-all shadow-sm"
                                                title="Edit User"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => setUserToDelete(user)}
                                                className="p-2.5 text-slate-400 bg-white ring-1 ring-transparent hover:text-rose-600 hover:bg-rose-50 hover:ring-rose-200 rounded-xl transition-all shadow-sm"
                                                title="Delete User"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {team.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="py-16 text-center">
                                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-slate-200 shadow-sm">
                                            <Users size={24} className="text-slate-400" />
                                        </div>
                                        <p className="text-lg font-bold text-slate-900 mb-1">No Team Members</p>
                                        <p className="text-sm font-medium text-slate-500">Click the button above to add your first user.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div> */}

            {/* --- ADD / EDIT USER MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0" onClick={() => setIsModalOpen(false)}></div>
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                        <div className="px-8 py-6 border-b border-slate-100 bg-white flex justify-between items-center z-20">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                                    <UserPlus size={20} strokeWidth={2.5} />
                                </div>
                                <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                                    {isEditing ? 'Edit Team Member' : 'Add Team Member'}
                                </h3>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors">
                                <X size={20} strokeWidth={2.5} />
                            </button>
                        </div>

                        <form onSubmit={handleFormSubmit} className="flex flex-col">
                            <div className="p-8 space-y-6 bg-slate-50/30">
                                <div>
                                    <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Full Name <span className="text-rose-500">*</span></label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="e.g. Jane Doe"
                                        className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Email Address <span className="text-rose-500">*</span></label>
                                    <input
                                        required
                                        type="email"
                                        placeholder="jane@company.com"
                                        className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">System Role</label>
                                        <div className="relative">
                                            <select
                                                className="w-full appearance-none bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all cursor-pointer"
                                                value={formData.role}
                                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            >
                                                <option value="Admin">Admin</option>
                                                <option value="Reviewer">Reviewer</option>
                                                <option value="User">User</option>
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Account Status</label>
                                        <div className="relative">
                                            <select
                                                className="w-full appearance-none bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all cursor-pointer"
                                                value={formData.status}
                                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            >
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="px-8 py-6 border-t border-slate-100 flex justify-end gap-3 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-3 text-sm font-bold text-slate-600 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-all shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-8 py-3 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg active:scale-95"
                                >
                                    {isEditing ? 'Save Changes' : 'Add Member'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- DELETE CONFIRMATION MODAL --- */}
            {userToDelete && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={() => setUserToDelete(null)}></div>
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-8 pb-6 text-center">
                            <div className="w-20 h-20 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-6 ring-8 ring-rose-50/50">
                                <AlertTriangle size={36} strokeWidth={2} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Remove User?</h3>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed">
                                Are you sure you want to remove <span className="font-bold text-slate-800">{userToDelete.name}</span> from the team? They will immediately lose access to the platform.
                            </p>
                        </div>
                        <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => setUserToDelete(null)}
                                className="flex-1 px-4 py-3.5 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
                            >
                                Keep User
                            </button>
                            <button
                                onClick={executeDelete}
                                className="flex-1 px-4 py-3.5 text-sm font-bold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-md shadow-rose-600/20 active:scale-95 transition-all"
                            >
                                Yes, Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default TeamManagementView;